/**
 * Live timer component that shows elapsed time since start.
 * Updates every second when active.
 */

import { useState, useEffect, useCallback } from 'react';

interface TimerProps {
  startTime: string;
  className?: string;
}

export function Timer({ startTime, className = '' }: TimerProps) {
  const [elapsed, setElapsed] = useState(() => calculateElapsed(startTime));

  useEffect(() => {
    // Update immediately
    setElapsed(calculateElapsed(startTime));

    // Then update every second
    const interval = setInterval(() => {
      setElapsed(calculateElapsed(startTime));
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  return (
    <div className={`font-mono text-4xl font-bold text-status-in-progress ${className}`}>
      {formatElapsed(elapsed)}
    </div>
  );
}

/**
 * Calculate elapsed seconds since start time.
 */
function calculateElapsed(startTime: string): number {
  const start = new Date(startTime).getTime();
  const now = Date.now();
  return Math.floor((now - start) / 1000);
}

/**
 * Format elapsed seconds as HH:MM:SS.
 */
function formatElapsed(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts = [
    hours.toString().padStart(2, '0'),
    minutes.toString().padStart(2, '0'),
    seconds.toString().padStart(2, '0'),
  ];

  return parts.join(':');
}

/**
 * Compact timer display for lists.
 */
export function TimerCompact({ startTime, className = '' }: TimerProps) {
  const [elapsed, setElapsed] = useState(() => calculateElapsed(startTime));

  useEffect(() => {
    setElapsed(calculateElapsed(startTime));
    const interval = setInterval(() => {
      setElapsed(calculateElapsed(startTime));
    }, 1000);
    return () => clearInterval(interval);
  }, [startTime]);

  return (
    <span className={`font-mono text-sm text-status-in-progress ${className}`}>
      {formatElapsedCompact(elapsed)}
    </span>
  );
}

/**
 * Format elapsed time compactly (1h 23m or 45m or 30s).
 */
function formatElapsedCompact(totalSeconds: number): string {
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes}m`;
}
